import React, { useState, useEffect } from 'react';
import { Bookmark, MessageSquare, BrainCircuit, BarChart3, Clock, Trash2, Loader2, ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn, getRoomPath, getDiscussionPath } from '../utils';
import { api } from '../api';
import EmptyState from '../components/EmptyState';

const SavedItem = ({ id, type, title, community, date, path, onRemove }) => (
  <div className="bg-white p-6 rounded-3xl border border-gray-100 hover:border-brand-indigo/20 hover:shadow-xl hover:shadow-brand-indigo/5 transition-all active:scale-[0.99] group relative overflow-hidden">
    <div className="flex items-start gap-4">
      <div className={cn(
        "w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 shadow-sm transition-transform group-hover:scale-110",
        type === 'qa' || type === 'question' ? "bg-purple-50 text-purple-600" :
        type === 'brainstorm' || type === 'idea' ? "bg-green-50 text-green-600" : "bg-brand-indigo/10 text-brand-indigo"
      )}>
        {(type === 'qa' || type === 'question') && <MessageSquare size={20} />}
        {(type === 'brainstorm' || type === 'idea') && <BrainCircuit size={20} />}
        {(type === 'poll') && <BarChart3 size={20} />}
      </div>
      <div className="flex-1">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xxs font-bold uppercase tracking-[0.15em] text-gray-400">
            {type === 'question' ? 'Live Q&A' : type === 'idea' ? 'Brainstorming' : type === 'poll' ? 'Live Poll' : 'Saved Content'}
          </span>
          <button 
            onClick={() => onRemove(id)}
            className="text-gray-300 hover:text-red-500 transition-colors"
          >
            <Trash2 size={16} />
          </button>
        </div>
        <Link to={path || "#"}>
          <h3 className="text-lg font-bold text-gray-900 group-hover:text-brand-indigo transition-colors leading-tight mb-2">
            {title}
          </h3>
        </Link>
        <div className="flex items-center gap-4 text-xs font-medium text-gray-500">
          <span className="flex items-center gap-1">
            <Clock size={14} />
            {date}
          </span>
          {community && <span className="font-bold text-gray-400">@{community}</span>}
        </div>
      </div>
    </div>
  </div>
);

const Saved = () => {
  const [bookmarks, setBookmarks] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchBookmarks = async () => {
    try {
      setLoading(true);
      const data = await api.get('/bookmarks');
      setBookmarks(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch bookmarks', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookmarks();
  }, []);

  const handleRemove = async (id) => {
    try {
      await api.delete(`/bookmarks/${id}`);
      setBookmarks(prev => Array.isArray(prev) ? prev.filter(b => b.id !== id) : []);
    } catch (err) {
      console.error('Failed to remove bookmark', err);
    }
  };

  const getBookmarkType = (b) => {
    const type = b.bookmarkable_type.split('\\').pop().toLowerCase();
    return type;
  };

  const getBookmarkPath = (b) => {
    const type = getBookmarkType(b);
    if (type === 'room' && b.bookmarkable) {
      return getRoomPath(b.bookmarkable);
    }
    if (type === 'discussion' && b.bookmarkable) {
      return getDiscussionPath(b.bookmarkable);
    }
    return '#';
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return `Saved ${date.toLocaleDateString()}`;
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-12">
      <div className="flex flex-col md:flex-row items-center justify-between gap-6 mb-10 text-center md:text-left">
        <div className="flex flex-col items-center md:items-start">
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight flex items-center gap-3">
            <Bookmark className="text-brand-indigo" size={32} />
            Saved Knowledge
          </h1>
          <p className="text-gray-500 font-medium mt-1">Access your bookmarked sessions, ideas, and discussions.</p>
        </div>
        {!loading && (
          <div className="bg-brand-indigo/5 px-4 py-2 rounded-2xl border border-brand-indigo/10 shrink-0">
            <span className="text-sm font-bold text-brand-indigo">{Array.isArray(bookmarks) ? bookmarks.length : 0} Items</span>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4">
        {loading ? (
          <div className="p-20 flex flex-col items-center justify-center text-gray-400">
            <Loader2 size={40} className="animate-spin mb-4 text-indigo-600" />
            <p className="font-bold uppercase tracking-[0.15em] text-xs">Loading bookmarks...</p>
          </div>
        ) : (Array.isArray(bookmarks) && bookmarks.length > 0) ? (
          bookmarks.map(b => (
            <SavedItem 
              key={b.id}
              id={b.id}
              type={getBookmarkType(b)}
              title={b.bookmarkable?.title || b.bookmarkable?.name || b.bookmarkable?.content || 'Untitled'}
              community={b.bookmarkable?.community?.name}
              date={formatTime(b.created_at)}
              path={getBookmarkPath(b)}
              onRemove={handleRemove}
            />
          ))
        ) : (
          <EmptyState 
            icon={Bookmark}
            title="No bookmarks yet"
            description="Save sessions, ideas, and discussions to access them later from your personal vault."
            action={
              <Link to="/explore" className="inline-flex items-center gap-2 bg-brand-indigo text-white px-8 py-3 rounded-xl font-bold text-sm shadow-lg shadow-brand-indigo/20 hover:bg-brand-indigo/90 transition-all">
                Discover Content <ArrowUpRight size={18} />
              </Link>
            }
          />
        )}
      </div>

      {!loading && Array.isArray(bookmarks) && bookmarks.length > 0 && (
        <div className="mt-12 p-8 bg-gray-50 rounded-3xl border border-gray-100 text-center">
          <p className="text-sm font-bold text-gray-400 uppercase tracking-[0.15em] mb-4">You've reached the end</p>
          <Link to="/explore" className="text-brand-indigo font-bold hover:underline transition-all">
            Explore more sessions
          </Link>
        </div>
      )}
    </div>
  );
};

export default Saved;
